
import {
  computed, defineComponent, reactive, ref,
} from 'vue';
import { IParametrosConsultaRapida } from '@/core/models/Consulta/IParametrosConsultaRapida';
import { useTelaBase } from '@/core/composables/TelaBase';
import UtilitarioGeral from '@/core/utilitarios/UtilitarioGeral';
import ServicoCaracteristica from '@/servicos/Cadastros/Produtos/ServicoCaracteristica';
import storeSistema from '@/store/storeSistema';
import { IItemConsultaRapida } from '@/core/models/Consulta/IItemConsultaRapida';

export default defineComponent({
  name: 'SelecionarCaracteristicaCor',
  props: {
    varios: {
      type: Boolean,
      default: false,
    },
    codigoSelecionado: {
      type: Number || undefined,
    },
    codigosSelecionados: {
      type: Array as () => number[],
      default: () => [],
    },
    codigoCaracteristica: {
      type: Number,
      default: 0,
    },
    empresas: {
      type: Array as () => number[],
      default: () => [],
    },
    limparSelecao: {
      type: Boolean,
      default: true,
    },
    placeholder: {
      type: String,
      default: 'Digite para selecionar',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:codigoSelecionado', 'update:codigosSelecionados', 'change'],
  setup(props, { emit }) {
    const refSelecionarCaracteristica = ref<HTMLElement | null>(null);

    let debounce = 0;
    const servicoCaracteristica = new ServicoCaracteristica();

    const state = reactive({
      listaCaracteristicas: [] as IItemConsultaRapida[],
      buscandoDados: false,
      emDigitacao: false,
    });

    async function obterCaracteristicaPorCodigo(valor: any) {
      if (state.emDigitacao) { return; }
      if (!UtilitarioGeral.valorValido(valor)) { return; }
      if (!(valor > 0)) { return; }

      if (props.varios) {
        if (props.codigosSelecionados.length === 0) { return; }
      } else if (props.codigoSelecionado === 0) {
        return;
      }
      let efetuarConsulta = true;

      if (UtilitarioGeral.validaLista(state.listaCaracteristicas)) {
        if (props.varios) {
          const listaCodigosConsulta = state.listaCaracteristicas.map((c) => c.codigo);
          const diferencas = props.codigosSelecionados.filter((c) => !listaCodigosConsulta.includes(c));
          if (!UtilitarioGeral.validaLista(diferencas)) {
            efetuarConsulta = false;
          }
        } else {
          const existeLista = state.listaCaracteristicas.find((c) => c.codigo === props.codigoSelecionado);
          if (existeLista !== undefined) {
            efetuarConsulta = false;
          }
        }
      }

      if (!efetuarConsulta) return;

      state.listaCaracteristicas = [];

      const parametrosConsultaRapida: IParametrosConsultaRapida = {
        apenasAtivos: false, recursoAssociado: '',
      };

      if (UtilitarioGeral.validaLista(props.empresas)) {
        parametrosConsultaRapida.empresas = props.empresas;
      } else {
        parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
      }

      if (props.varios) {
        parametrosConsultaRapida.filtrarPorVariosCodigos = true;
        parametrosConsultaRapida.valores = valor;
      } else {
        parametrosConsultaRapida.filtrarPorCodigo = true;
        parametrosConsultaRapida.valor = valor;
      }

      const listaCaracteristicas = await servicoCaracteristica.consultaRapidaItens(props.codigoCaracteristica, parametrosConsultaRapida);
      state.listaCaracteristicas = listaCaracteristicas;
      if (!UtilitarioGeral.validaLista(listaCaracteristicas)) {
        if (props.varios) {
          emit('update:codigosSelecionados', undefined);
        } else {
          emit('update:codigoSelecionado', undefined);
        }
      }
    }

    async function pesquisarCaracteristica(valorPesquisa: any) {
      state.emDigitacao = true;

      state.buscandoDados = true;
      state.listaCaracteristicas = [];
      clearTimeout(debounce);
      debounce = setTimeout(async () => {
        const parametrosConsultaRapida: IParametrosConsultaRapida = {
          valor: valorPesquisa, apenasAtivos: true, recursoAssociado: '', quantidadeRegistros: 100,
        };

        if (UtilitarioGeral.validaLista(props.empresas)) {
          parametrosConsultaRapida.empresas = props.empresas;
        } else {
          parametrosConsultaRapida.empresas = storeSistema.getters.empresasUsuarioAutenticado();
        }

        state.listaCaracteristicas = await servicoCaracteristica.consultaRapidaItens(props.codigoCaracteristica, parametrosConsultaRapida);
        state.buscandoDados = false;
      }, 600);
    }

    function obterCaracteristica(): any {
      if (props.codigoSelecionado !== undefined) {
        if (props.codigoSelecionado > 0) {
          obterCaracteristicaPorCodigo(props.codigoSelecionado);
        } else {
          return undefined;
        }
      }
      return props.codigoSelecionado;
    }

    const computedCodigoSelecionado = computed({
      get: () => obterCaracteristica(),
      set: (val: number) => {
        emit('update:codigoSelecionado', val);
      },
    });

    function obterCaracteristicas(): number[] {
      obterCaracteristicaPorCodigo(props.codigosSelecionados);
      return props.codigosSelecionados;
    }

    const computedCodigosSelecionados = computed({
      get: () => obterCaracteristicas(),
      set: (val: number[]) => {
        emit('update:codigosSelecionados', val);
      },
    });

    function change(event: any) {
      emit('change', event);
    }

    function focus() {
      if (refSelecionarCaracteristica.value) {
        refSelecionarCaracteristica.value.focus();
      }
    }

    return {
      props,
      state,
      refSelecionarCaracteristica,
      pesquisarCaracteristica,
      computedCodigoSelecionado,
      computedCodigosSelecionados,
      change,
      focus,
    };
  },
});

